import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import { useAgencyStore } from 'Application/store';
import { ThemeMode } from 'config';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as Yup from 'yup';

interface CancelTravelScheduleProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    svNumber: string;
    intermediary: string;
    page?: number;
    pageSize?: number;
}

const validationSchema = Yup.object().shape({
    cancellationReason: Yup.string().required('Please provide reason for the cancellation')
});

const CancelTravelSchedule: FC<CancelTravelScheduleProps> = ({ isOpen, setIsOpen, svNumber, intermediary, page, pageSize }) => {
    const { isLoading, error: cancelTravelScheduleError, cancelTravelSchedule } = useAgencyStore();

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';
    const textColor = palette.mode === ThemeMode.DARK ? '#718096' : '#000';

    const initialValues = {
        cancellationReason: ''
    };

    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema)
    });

    const handleClose = () => {
        setIsOpen(false);
    };

    const onSubmit = async (formData: any) => {
        const values: typeof initialValues = formData;

        try {
            await cancelTravelSchedule({ svNumber, cancellationReason: values.cancellationReason }, intermediary, page, pageSize);
            toast.success('Successfully cancelled the travel schedule.');
            handleClose();
        } catch (error) {
            toast.error(cancelTravelScheduleError);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Cancel travel schedule</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>
                    Please provide the reason for cancelling the travel schedule
                </div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box className="mb-4">
                        <Typography sx={{ color: textColor }} className="mb-2 text-[1.1em]">
                            Reason for cancellation
                        </Typography>
                        <Controller
                            name="cancellationReason"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    type="text"
                                    fullWidth
                                    error={!!errors.cancellationReason}
                                    helperText={errors.cancellationReason?.message}
                                    onChange={(e) => field.onChange(e)}
                                />
                            )}
                        />
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                            disabled={isLoading} // Disable button while loading
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading} // Disable button while loading
                        >
                            Submit
                        </Button>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CancelTravelSchedule;
